import { useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import {
  KeyValue, Pill, Spinner, FormGrid, Field, TextInput, NumberInput, DateInput,
  TextArea, SelectInput, LineTable,
} from '../../components/ui';
import { SearchSelect } from '../../components/SearchSelect';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import {
  money, num, dateLabel, purchaseStatus, purchaseType, purchaseTypeFull, chargeType,
  customerShort,
} from '../../lib/format';
import type {
  PurchaseOrder, PurchaseOrderLine, PurchaseOrderCharge, PurchaseReceipt,
  Item, Supplier, Warehouse,
} from '../../lib/types';

const today = () => new Date().toISOString().slice(0, 10);

const CHARGE_OPTIONS: { value: string; label: string }[] = [
  { value: 'international_freight', label: '国际运费' },
  { value: 'insurance', label: '保险' },
  { value: 'customs_duty', label: '关税' },
  { value: 'statistical_fee', label: '统计税' },
  { value: 'customs_broker', label: '报关费' },
  { value: 'local_freight', label: '国内运费' },
  { value: 'iva', label: 'IVA 增值税' },
  { value: 'iva_perception', label: 'IVA 预扣' },
  { value: 'iibb_perception', label: '收入总额预扣' },
  { value: 'internal_tax', label: '内部税' },
  { value: 'other', label: '其他费用' },
];

// —— 收货入库面板：按未收数量分批收货，单事务过账 receipt 库存流水并回填 PO 状态 ——
interface ReceiptLineState { qty: string; whId: string; whLabel: string; }
function ReceivePanel({ po, onDone, onCancel }: { po: PurchaseOrder; onDone: () => void; onCancel: () => void }) {
  const openLines = (po.lines ?? []).filter((l) => Number(l.quantity) - Number(l.received_quantity) > 1e-9);
  const [state, setState] = useState<Record<string, ReceiptLineState>>(() => {
    const init: Record<string, ReceiptLineState> = {};
    for (const l of po.lines ?? []) {
      const remaining = Number(l.quantity) - Number(l.received_quantity);
      init[l.id] = { qty: remaining > 0 ? String(remaining) : '0', whId: '', whLabel: '' };
    }
    return init;
  });
  const [receiptDate, setReceiptDate] = useState(today());
  const [supplierDocument, setSupplierDocument] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const patch = (id: string, p: Partial<ReceiptLineState>) =>
    setState((s) => ({ ...s, [id]: { ...s[id], ...p } }));

  const submit = async () => {
    setErr(null);
    if (!receiptDate.trim()) { setErr('收货日期为必填'); return; }
    const lines = openLines
      .map((l) => ({ l, st: state[l.id] }))
      .filter(({ st }) => Number(st.qty) > 0)
      .map(({ l, st }) => {
        const remaining = Number(l.quantity) - Number(l.received_quantity);
        const qty = Math.min(Number(st.qty), remaining);
        return { purchase_order_line_id: l.id, quantity: qty, to_warehouse_id: st.whId, uom: l.uom };
      });
    if (!lines.length) { setErr('请至少填写一条大于 0 的收货数量'); return; }
    if (lines.some((x) => !x.to_warehouse_id)) { setErr('每条收货明细都需选择收货仓库'); return; }
    setBusy(true);
    try {
      await api.post(`/api/purchase-orders/${po.id}/receipts`, {
        receipt_date: receiptDate,
        supplier_document: supplierDocument || null,
        notes: notes || null,
        lines,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '收货入库失败');
    } finally { setBusy(false); }
  };

  if (!openLines.length) {
    return (
      <div className="notice ok" style={{ display: 'block', marginTop: 10 }}>
        该采购订单所有明细均已收满，无需继续收货。
      </div>
    );
  }

  return (
    <div className="notice info" style={{ display: 'block', marginTop: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>收货入库（生成 GR 收货单 → 过账采购入库库存流水 → 累加已收数量并重算状态；不改动物料采购成本）</div>
      {err ? <div className="notice bad" style={{ marginBottom: 8 }}>{err}</div> : null}
      <FormGrid min={160}>
        <Field label="收货日期" required><DateInput value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} /></Field>
        <Field label="供应商单据号（发票/送货单）"><TextInput value={supplierDocument} onChange={(e) => setSupplierDocument(e.target.value)} placeholder="留痕，便于对账" /></Field>
      </FormGrid>
      <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '8px 0 4px' }}>未收明细（默认带出剩余数量，可按到货情况修改）</div>
      <LineTable<PurchaseOrderLine>
        rows={openLines}
        emptyText="暂无未收明细"
        columns={[
          { header: '物料', render: (l) => `${l.item_code ?? ''} ${l.item_name ?? ''}`.trim() || l.item_id },
          { header: '订购/已收', align: 'right', render: (l) => <span className="num">{num(l.received_quantity, 3)} / {num(l.quantity, 3)}</span> },
          { header: '本次收货', render: (l) => (
            <NumberInput value={state[l.id]?.qty ?? '0'} onChange={(e) => patch(l.id, { qty: e.target.value })} />
          ) },
          { header: '收货仓库', render: (l) => (
            <SearchSelect<Warehouse> endpoint="/api/warehouses" value={state[l.id]?.whId || null} label={state[l.id]?.whLabel ?? ''}
              onChange={(row) => patch(l.id, { whId: row?.id ?? '', whLabel: row ? `${row.code} ${row.name_zh}` : '' })}
              optionTitle={(w) => `${w.code} · ${w.name_zh}`} placeholder="选择仓库…" />
          ) },
        ]}
      />
      <FormGrid min={220} >
        <Field label="收货备注" span><TextInput value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      </FormGrid>
      <div className="detail-actions">
        <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '过账中…' : '确认收货入库'}</button>
        <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
      </div>
    </div>
  );
}

// —— 采购订单详情：关键字段 + 明细 + 进口费用 + 已生成收货单 + 开单收货 ——
function PurchaseDetail({ row, onChanged }: { row: PurchaseOrder; onChanged: () => void }) {
  const { user } = useAuth();
  const canWrite = can(user, 'purchase-orders', 'create');
  const { data, loading, error, reload } = useDetail<PurchaseOrder>(`/api/purchase-orders/${row.id}`);
  const [receiving, setReceiving] = useState(false);
  const po = data ?? row;
  const lines = po.lines ?? [];
  const charges = po.charges ?? [];
  const receipts = po.receipts ?? [];
  const ordered = lines.reduce((s, l) => s + Number(l.quantity), 0);
  const received = lines.reduce((s, l) => s + Number(l.received_quantity), 0);
  const allReceived = ordered > 0 && received >= ordered - 1e-9;
  const open = po.status !== 'cancelled' && po.status !== 'received';

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{po.number}</span>
        <h3>{po.supplier_name || po.supplier_id}</h3>
        <Pill tone={purchaseTypeFull(po.purchase_type).tone} label={purchaseTypeFull(po.purchase_type).label} />
        <Pill tone={purchaseStatus(po.status).tone} label={purchaseStatus(po.status).label} />
        <div className="spacer" />
        {loading ? <Spinner label="刷新…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      <div className="kv-grid">
        <KeyValue k="供应商">{po.supplier_name || po.supplier_id}</KeyValue>
        <KeyValue k="订单日期">{dateLabel(po.order_date)}</KeyValue>
        <KeyValue k="预计到货">{dateLabel(po.expected_date)}</KeyValue>
        <KeyValue k="币种 / 汇率">{`${po.currency} · ${num(po.exchange_rate, 4)}`}</KeyValue>
        <KeyValue k="小计">{money(po.subtotal, po.currency)}</KeyValue>
        <KeyValue k="税额">{money(po.tax_total, po.currency)}</KeyValue>
        <KeyValue k="合计">{money(po.grand_total, po.currency)}</KeyValue>
        <KeyValue k="预估本币(ARS)">{money(po.estimated_total_ars, 'ARS')}</KeyValue>
        <KeyValue k="收货进度">{`${num(received, 3)} / ${num(ordered, 3)}`}</KeyValue>
        <KeyValue k="ERPNext 编号">{po.legacy_erpnext_id || '—'}</KeyValue>
        <KeyValue k="备注">{po.notes || '—'}</KeyValue>
      </div>

      {po.purchase_type === 'import' ? (
        <div className="notice info" style={{ marginTop: 10, display: 'block' }}>
          <div style={{ fontSize: 12.5 }}>进口采购：费用已按原始币种 + 汇率单独留存，折算 ARS 仅用于预估。运费/关税等到岸成本分摊规则待 P3-2 / P3-3 落地，本收货流程不改动 items.purchase_cost。</div>
        </div>
      ) : null}

      <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '12px 0 4px' }}>采购明细（{lines.length}）</div>
      <LineTable<PurchaseOrderLine>
        rows={lines}
        emptyText="暂无明细"
        columns={[
          { header: '物料', render: (l) => `${l.item_code ?? ''} ${l.item_name ?? ''}`.trim() || '—' },
          { header: '数量', align: 'right', render: (l) => <span className="num">{num(l.quantity, 3)}</span> },
          { header: '已收', align: 'right', render: (l) => <span className="num">{num(l.received_quantity, 3)}</span> },
          { header: '单位', render: (l) => <span className="muted">{l.uom}</span> },
          { header: '单价', align: 'right', render: (l) => <span className="num">{money(l.unit_price, po.currency)}</span> },
          { header: '税率%', align: 'right', render: (l) => <span className="num">{num(l.tax_rate, 2)}</span> },
          { header: '行合计', align: 'right', render: (l) => <span className="num">{money(l.line_total, po.currency)}</span> },
        ]}
      />

      {charges.length ? (
        <>
          <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '12px 0 4px' }}>采购费用（{charges.length}）· 原始币种 + 折算依据</div>
          <LineTable<PurchaseOrderCharge>
            rows={charges}
            emptyText="暂无费用"
            columns={[
              { header: '费用类型', render: (c) => chargeType(c.charge_type) },
              { header: '说明', render: (c) => <span className="muted">{c.description || '—'}</span> },
              { header: '币种', render: (c) => <span className="muted">{c.currency}</span> },
              { header: '汇率', align: 'right', render: (c) => <span className="num">{num(c.exchange_rate, 4)}</span> },
              { header: '金额', align: 'right', render: (c) => <span className="num">{money(c.amount, c.currency)}</span> },
              { header: '折算ARS', align: 'right', render: (c) => <span className="num">{money(c.amount_ars, 'ARS')}</span> },
              { header: '可抵扣', render: (c) => c.recoverable ? <Pill tone="ok" label="可抵扣" /> : <span className="muted">—</span> },
            ]}
          />
        </>
      ) : null}

      {receipts.length ? (
        <>
          <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '12px 0 4px' }}>收货单（{receipts.length}）· 每张已生成一条采购入库库存流水</div>
          <LineTable<PurchaseReceipt>
            rows={receipts}
            emptyText="暂无收货记录"
            columns={[
              { header: '收货单号', render: (r) => <span className="num strong">{r.number}</span> },
              { header: '收货日期', render: (r) => <span className="muted">{dateLabel(r.receipt_date)}</span> },
              { header: '供应商单据', render: (r) => <span className="muted">{r.supplier_document || '—'}</span> },
              { header: '状态', render: (r) => <Pill tone={r.status === 'posted' ? 'ok' : 'neutral'} label={r.status === 'posted' ? '已入库' : r.status === 'cancelled' ? '已取消' : r.status} /> },
              { header: '备注', render: (r) => <span className="muted">{r.notes || '—'}</span> },
            ]}
          />
        </>
      ) : null}

      {canWrite && open ? (
        <div className="detail-actions" style={{ marginTop: 10 }}>
          <button className="btn primary" disabled={allReceived} onClick={() => setReceiving((v) => !v)}>
            {receiving ? '收起收货' : (allReceived ? '已全部收货' : '收货入库')}
          </button>
        </div>
      ) : null}

      {receiving && !allReceived ? (
        <ReceivePanel
          po={po}
          onCancel={() => setReceiving(false)}
          onDone={() => { setReceiving(false); reload(); onChanged(); }}
        />
      ) : null}
    </div>
  );
}

// —— 新建采购订单：国内(ARS) / 进口(外币+汇率)，多物料明细，进口可录费用分类 ——
interface PoLineRow { itemId: string; label: string; quantity: string; uom: string; unitPrice: string; taxRate: string; description: string; }
interface ChargeRow { chargeType: string; description: string; amount: string; currency: string; exchangeRate: string; recoverable: boolean; }

function NewPurchaseForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [supplierLabel, setSupplierLabel] = useState('');
  const [type, setType] = useState<'domestic' | 'import'>('domestic');
  const [f, setF] = useState<Record<string, string>>({ order_date: today(), expected_date: '', currency: 'USD', exchange_rate: '' });
  const [lines, setLines] = useState<PoLineRow[]>([{ itemId: '', label: '', quantity: '1', uom: 'Unidad', unitPrice: '', taxRate: '21', description: '' }]);
  const [charges, setCharges] = useState<ChargeRow[]>([]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: string) => setF((d) => ({ ...d, [k]: v }));
  const isImport = type === 'import';

  const setLine = (i: number, p: Partial<PoLineRow>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...p } : l)));
  const setCharge = (i: number, p: Partial<ChargeRow>) => setCharges((cs) => cs.map((c, j) => (j === i ? { ...c, ...p } : c)));

  const submit = async () => {
    setErr(null);
    if (!supplierId) { setErr('供应商为必填'); return; }
    if (!f.order_date?.trim()) { setErr('订单日期为必填'); return; }
    const validLines = lines.filter((l) => l.itemId && Number(l.quantity) > 0 && l.unitPrice !== '');
    if (!validLines.length) { setErr('至少需要一条有效明细（选择物料、数量>0、填写单价）'); return; }
    if (isImport) {
      if (!f.currency?.trim()) { setErr('进口采购需填写外币币种'); return; }
      if (!(Number(f.exchange_rate) > 0)) { setErr('进口采购需填写大于 0 的汇率'); return; }
    }
    setBusy(true);
    try {
      await api.post('/api/purchase-orders', {
        supplier_id: supplierId,
        order_date: f.order_date,
        expected_date: f.expected_date || null,
        purchase_type: type,
        currency: isImport ? f.currency : 'ARS',
        exchange_rate: isImport ? Number(f.exchange_rate) : 1,
        notes: notes || null,
        lines: validLines.map((l) => ({
          item_id: l.itemId, quantity: Number(l.quantity), uom: l.uom || 'Unidad',
          unit_price: Number(l.unitPrice), tax_rate: Number(l.taxRate || 0), description: l.description || null,
        })),
        charges: isImport ? charges
          .filter((c) => c.amount !== '' && Number(c.amount) > 0)
          .map((c) => ({
            charge_type: c.chargeType, description: c.description || null,
            amount: Number(c.amount), currency: c.currency || 'ARS',
            exchange_rate: c.currency === 'ARS' ? 1 : Number(c.exchangeRate || 1),
            recoverable: c.recoverable,
          })) : [],
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建失败');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head">
        <div>
          <h2>新建采购订单</h2>
          <div className="desc">写入核心服务 /api/purchase-orders · 国内采购固定 ARS 计价；进口采购以外币计价并记录汇率。收货在订单展开处「收货入库」完成（生成库存流水）。本步骤不改动物料采购成本</div>
        </div>
      </div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <FormGrid>
          <Field label="供应商" required>
            <SearchSelect<Supplier> endpoint="/api/suppliers" value={supplierId} label={supplierLabel}
              onChange={(row) => { setSupplierId(row?.id ?? null); setSupplierLabel(row ? `${row.code} ${row.legal_name}` : ''); }}
              optionTitle={(s) => `${s.code} · ${s.legal_name}`} optionMeta={(s) => s.tax_id} placeholder="检索供应商…" />
          </Field>
          <Field label="采购类型" required>
            <SelectInput value={type} onChange={(e) => setType(e.target.value as 'domestic' | 'import')}>
              <option value="domestic">国内采购（ARS）</option>
              <option value="import">进口采购（外币）</option>
            </SelectInput>
          </Field>
          <Field label="订单日期" required><DateInput value={f.order_date} onChange={(e) => set('order_date', e.target.value)} /></Field>
          <Field label="预计到货日"><DateInput value={f.expected_date} onChange={(e) => set('expected_date', e.target.value)} /></Field>
          {isImport ? (
            <>
              <Field label="外币币种" required><TextInput value={f.currency} onChange={(e) => set('currency', e.target.value)} placeholder="USD / EUR / CNY…" /></Field>
              <Field label="汇率（→ARS）" required><NumberInput value={f.exchange_rate} onChange={(e) => set('exchange_rate', e.target.value)} placeholder="如 1000" /></Field>
            </>
          ) : (
            <Field label="计价币种"><TextInput value="ARS（国内固定）" disabled onChange={() => {}} /></Field>
          )}
          <Field label="备注" span><TextArea value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        </FormGrid>

        <div className="detail-head" style={{ margin: '14px 0 6px' }}><h3 style={{ fontSize: 14 }}>采购明细（{isImport ? '按外币单价' : '按 ARS 单价'}）</h3><div className="spacer" />
          <button className="btn link" onClick={() => setLines((ls) => [...ls, { itemId: '', label: '', quantity: '1', uom: 'Unidad', unitPrice: '', taxRate: '21', description: '' }])}>+ 添加明细</button>
        </div>
        {lines.map((l, i) => (
          <div key={i} className="form-row" style={{ display: 'grid', gridTemplateColumns: '2.2fr 0.9fr 0.9fr 1.1fr 0.8fr 1.4fr auto', gap: 8, marginBottom: 6, alignItems: 'end' }}>
            <Field label={`物料 ${i + 1}`}>
              <SearchSelect<Item> endpoint="/api/items" value={l.itemId || null} label={l.label}
                onChange={(row) => setLine(i, { itemId: row?.id ?? '', label: row ? `${row.code} ${row.name_zh}` : '', uom: row && !l.uom ? row.stock_uom : l.uom })}
                optionTitle={(it) => `${it.code} · ${it.name_zh}`} placeholder="检索物料…" />
            </Field>
            <Field label="数量"><NumberInput value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></Field>
            <Field label="单位"><TextInput value={l.uom} onChange={(e) => setLine(i, { uom: e.target.value })} /></Field>
            <Field label="单价"><NumberInput value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} /></Field>
            <Field label="税率%"><NumberInput value={l.taxRate} onChange={(e) => setLine(i, { taxRate: e.target.value })} /></Field>
            <Field label="说明"><TextInput value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} /></Field>
            <button className="btn" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} title="删除该明细">×</button>
          </div>
        ))}

        {isImport ? (
          <>
            <div className="detail-head" style={{ margin: '14px 0 6px' }}><h3 style={{ fontSize: 14 }}>采购费用（运费/关税等，选填）</h3><div className="spacer" />
              <button className="btn link" onClick={() => setCharges((cs) => [...cs, { chargeType: 'international_freight', description: '', amount: '', currency: 'USD', exchangeRate: f.exchange_rate || '', recoverable: false }])}>+ 添加费用</button>
            </div>
            {charges.map((c, i) => (
              <div key={i} className="form-row" style={{ display: 'grid', gridTemplateColumns: '1.6fr 1.4fr 1fr 0.9fr 0.9fr auto auto', gap: 8, marginBottom: 6, alignItems: 'end' }}>
                <Field label="费用类型">
                  <SelectInput value={c.chargeType} onChange={(e) => setCharge(i, { chargeType: e.target.value })}>
                    {CHARGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </SelectInput>
                </Field>
                <Field label="说明"><TextInput value={c.description} onChange={(e) => setCharge(i, { description: e.target.value })} /></Field>
                <Field label="金额"><NumberInput value={c.amount} onChange={(e) => setCharge(i, { amount: e.target.value })} /></Field>
                <Field label="币种"><TextInput value={c.currency} onChange={(e) => setCharge(i, { currency: e.target.value })} /></Field>
                <Field label="汇率"><NumberInput value={c.exchangeRate} onChange={(e) => setCharge(i, { exchangeRate: e.target.value })} /></Field>
                <label style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 12.5, paddingBottom: 8 }}>
                  <input type="checkbox" checked={c.recoverable} onChange={(e) => setCharge(i, { recoverable: e.target.checked })} />可抵扣
                </label>
                <button className="btn" onClick={() => setCharges((cs) => cs.filter((_, j) => j !== i))} title="删除该费用">×</button>
              </div>
            ))}
          </>
        ) : null}

        <div className="detail-actions">
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '创建采购订单'}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    </section>
  );
}

export function PurchasesPage() {
  const { user } = useAuth();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [creating, setCreating] = useState(false);
  const canWrite = can(user, 'purchase-orders', 'create');
  const bump = () => setReloadSignal((n) => n + 1);

  const columns: Column<PurchaseOrder>[] = [
    { header: '采购单号', render: (p) => <span className="num strong">{p.number}</span> },
    // 供应商名很长（"Sichuan Huayin Intelligent Manufacturing New Material Technology Co., LTD"），
    // 不限宽会把右侧「状态 / 编辑 / 删除」挤出可视区（2026-10-29 现场）。
    // 列表只显示前两个词，完整名称放 title。
    { header: '供应商', render: (p) => <span title={p.supplier_name || ''}>{customerShort(p.supplier_name)}</span> },
    { header: '类型', render: (p) => <Pill tone={purchaseType(p.purchase_type).tone} label={purchaseType(p.purchase_type).label} /> },
    { header: '订单日期', render: (p) => <span className="muted">{dateLabel(p.order_date)}</span> },
    { header: '币种', render: (p) => <span className="muted">{p.currency}</span> },
    { header: '合计', align: 'right', render: (p) => <span className="num">{money(p.grand_total, p.currency)}</span> },
    { header: '预估本币', align: 'right', render: (p) => <span className="num">{money(p.estimated_total_ars, 'ARS')}</span> },
    { header: '状态', render: (p) => <Pill tone={purchaseStatus(p.status).tone} label={purchaseStatus(p.status).label} /> },
  ];

  return (
    <>
      {creating ? (
        <NewPurchaseForm onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); bump(); }} />
      ) : null}
      <ListPage<PurchaseOrder>
        title="采购订单"
        desc="国内 / 进口采购 · /api/purchase-orders。展开查看明细、进口费用与收货单；「收货入库」生成 GR 并过账采购入库库存流水，累加已收数量并重算订单状态。供应商单据号在收货时留痕"
        endpoint="/api/purchase-orders"
        columns={columns}
        rowId={(p) => p.id}
        showYear={false}
        searchPlaceholder="搜索采购单号 / 供应商 / 状态…"
        reloadSignal={reloadSignal}
        rowActions={(row) => <RecordActions type="purchase-orders" row={row} fields={RECORD_FIELDS['purchase-orders']} label={String(row.number)} onDone={() => setReloadSignal((n) => n + 1)} />}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setCreating((v) => !v)}>{creating ? '收起新建' : '新建采购订单'}</button>
        ) : undefined}
        renderDetail={(row) => <PurchaseDetail row={row} onChanged={bump} />}
      />
    </>
  );
}
